import { MutationHookOptions, QueryHookOptions } from '@apollo/client';
import { useApiLazyQuery, useApiMutation, useApiQuery } from '@common/hooks';
import {
  checkBusinessUserByPerson,
  closeMonth,
  createAccountingTransfer,
  createBusinessPartner,
  createBusinessPartnerVignetteRange,
  createClaimPerson,
  createPersonAddress,
  createProvider,
  createUser,
  generateCloseMonthReport,
  getAccountingTransferLovs,
  getActivePersonsList,
  getActivePersonsWithoutBusinessPartnersList,
  getAdditionalInformationEnums,
  getAmendmentSubtypesQuery,
  getBusinessPartnerTypeEnumList,
  getBusinessUserLovs,
  getClosedMonthQuery,
  getCountriesList,
  getCurrenciesList,
  getLineRefDate,
  getPersonAddressDetails,
  getPersonAddressLovs,
  getPersonDetailsById,
  getPersonDetailsForm,
  getPersonDetailsList,
  getPersonEmailDetailsInfo,
  getPolicyPersonEnums,
  getProviderLOVs,
  getRelatedCompaniesList,
  getSystemReferenceDateQuery,
  getTitlesList,
  getVignetteRangeDetails,
  getVignetteRangeStatusLOV,
  getZonesList,
  updateBusinessPartner,
  updateBusinessPartnerVignetteRange,
  updatePersonAdditionalInfo,
  updatePersonAddress,
  updatePersonInfo,
  updateUser
} from '@common/services';
import {
  CheckBusinessUserByPersonResponse,
  CheckBusinessUserByPersonVariables,
  CloseMonthResponse,
  CloseMonthVariables,
  CreateAccoutingTransferResponse,
  CreateAccoutingTransferVariables,
  CreateAmendmentResponse,
  CreateAmendmentVariables,
  CreateBusinessPartnerResponse,
  CreateBusinessPartnerVariables,
  CreateBusinessPartnerVignetteRangeResponse,
  CreateBusinessPartnerVignetteRangeVariables,
  CreateCancellationAmendmentResponse,
  CreateClaimPersonResponse,
  CreateClaimPersonVariables,
  CreatePersonAddressResponse,
  CreatePersonAddressVariables,
  CreateProviderResponse,
  CreateProviderVariables,
  CreateUserResult,
  CreateUserVariables,
  GetAccountingTransferLovsResponse,
  GetActivePersonsListResponse,
  GetActivePersonsWithoutBusinessPartnersListResponse,
  GetBusinessPartnerTypeEnumListResponse,
  GetBusinessUserLovsResponse,
  GetClosedMonthResponse,
  GetClosedMonthVariables,
  GetCountriesListResponse,
  GetCurrenciesListResponse,
  GetLineRefDateResponse,
  GetPersonAddressDetailsResponse,
  GetPersonAddressDetailsVariables,
  GetPersonAddressLovsResponse,
  GetPersonDetailsByIdResponse,
  GetPersonDetailsByIdVariables,
  GetPersonDetailsFormResponse,
  GetPersonDetailsResponse,
  GetPersonDetailsVariables,
  GetPersonEmailDetailsInfoResponse,
  GetPersonEmailDetailsInfoVariables,
  GetProviderLovsListResponse,
  GetRelatedCompaniesListResponse,
  GetSystemReferenceDateResponse,
  GetTitlesListResponse,
  GetVignetteRangeDetailsResponse,
  GetVignetteRangeDetailsVariables,
  GetVignetteRangeStatusLOVResponse,
  GetZonesListResponse,
  OrderByInput,
  PaginationInput,
  UpdateBusinessPartnerResponse,
  UpdateBusinessPartnerVariables,
  UpdateBusinessPartnerVignetteRangeResponse,
  UpdateBusinessPartnerVignetteRangeVariables,
  UpdatePersonAdditionalInfoResponse,
  UpdatePersonAdditionalInfoVariables,
  UpdatePersonAddressResponse,
  UpdatePersonAddressVariables,
  UpdatePersonInfoResponse,
  UpdatePersonInfoVariables,
  UpdateUserResult,
  UpdateUserVariables
} from '@common/types';
import {
  createAmendmentMutation,
  createCancellationAmendmentMutation
} from '@features/production/amendment/services/amendment.mutations';
import {
  getBusinessPartnerDetails,
  getBusinessPartnersList,
  getBusinessPartnerVignetteRangeList
} from '@features/salesforce/business-partner';
import {
  getPersonDetailsAddressList,
  getPersonDetailsInfo
} from '@features/salesforce/persons';
import { getProvidersList } from '@features/salesforce/providers';
import { getUsersList } from '@features/salesforce/users';
import { searchClosedMonths } from '@features/settings/month-closing/services';

const useGetPersonDetailsForm = (
  options?: QueryHookOptions<GetPersonDetailsFormResponse>
) =>
  useApiQuery<GetPersonDetailsFormResponse>(
    getPersonDetailsForm,
    'getPersonDetailsForm',
    {
      fetchPolicy: 'cache-and-network', // or 'network-only' if you want fresh enums each time
      ...options
    },
    { withLoader: false }
  );

const useGetAdditionalInformationEnums = (
  options?: QueryHookOptions<GetPersonDetailsFormResponse>
) =>
  useApiQuery<GetPersonDetailsFormResponse>(
    getAdditionalInformationEnums,
    'getAdditionalInformationEnums',
    {
      fetchPolicy: 'cache-and-network', // or 'network-only' if you want fresh enums each time
      ...options
    },
    { withLoader: false }
  );

const useGetPersonAddressDetails = (
  options?: QueryHookOptions<
    GetPersonAddressDetailsResponse,
    GetPersonAddressDetailsVariables
  >
) =>
  useApiQuery<
    GetPersonAddressDetailsResponse,
    GetPersonAddressDetailsVariables
  >(
    getPersonAddressDetails,
    'getPersonAddressDetails',
    {
      ...options,
      fetchPolicy: 'cache-and-network'
    },
    { withLoader: false }
  );

const useGetPersonAddressLovs = (
  options?: QueryHookOptions<GetPersonAddressLovsResponse>
) =>
  useApiQuery<GetPersonAddressLovsResponse>(
    getPersonAddressLovs,
    'getPersonAddressLovs',
    {
      ...options,
      fetchPolicy: 'cache-and-network'
    },
    { withLoader: false }
  );

const useGetActivePersonsWithoutBusinessPartnersList = (
  options?: QueryHookOptions<GetActivePersonsWithoutBusinessPartnersListResponse>
) =>
  useApiQuery<GetActivePersonsWithoutBusinessPartnersListResponse>(
    getActivePersonsWithoutBusinessPartnersList,
    'getActivePersonsWithoutBusinessPartnersList',
    {
      ...options,
      fetchPolicy: 'cache-and-network'
    },
    { withLoader: false }
  );

const useGetBusinessPartnerTypeEnumList = (
  options?: QueryHookOptions<GetBusinessPartnerTypeEnumListResponse>
) =>
  useApiQuery<GetBusinessPartnerTypeEnumListResponse>(
    getBusinessPartnerTypeEnumList,
    'getBusinessPartnerTypeEnumList',
    {
      ...options,
      fetchPolicy: 'cache-and-network'
    },
    { withLoader: false }
  );

const useGetCountriesList = (
  options?: QueryHookOptions<GetCountriesListResponse>
) =>
  useApiQuery<GetCountriesListResponse>(
    getCountriesList,
    'getCountriesList',
    {
      ...options,
      fetchPolicy: 'cache-and-network'
    },
    { withLoader: false }
  );

const useGetCurrenciesList = (
  options?: QueryHookOptions<GetCurrenciesListResponse>
) =>
  useApiQuery<GetCurrenciesListResponse>(
    getCurrenciesList,
    'getCurrenciesList',
    {
      ...options,
      fetchPolicy: 'cache-and-network'
    },
    { withLoader: false }
  );

const useGetPersonDetails = (
  options?: QueryHookOptions<
    GetPersonDetailsResponse,
    GetPersonDetailsVariables
  >
) =>
  useApiLazyQuery<GetPersonDetailsResponse, GetPersonDetailsVariables>(
    getPersonDetailsList,
    'getPersonDetailsList',
    {
      ...options,
      fetchPolicy: 'cache-and-network'
    }
  );

const useGetRelatedCompaniesList = (
  options?: QueryHookOptions<GetRelatedCompaniesListResponse>
) =>
  useApiQuery<GetRelatedCompaniesListResponse>(
    getRelatedCompaniesList,
    'getRelatedCompaniesList',
    {
      ...options,
      fetchPolicy: 'cache-and-network'
    },
    { withLoader: false }
  );

const useGetTitlesList = (options?: QueryHookOptions<GetTitlesListResponse>) =>
  useApiQuery<GetTitlesListResponse>(
    getTitlesList,
    'getTitlesList',
    {
      ...options,
      fetchPolicy: 'cache-and-network'
    },
    { withLoader: false }
  );

const useGetVignetteRangeStatusLOV = (
  options?: QueryHookOptions<GetVignetteRangeStatusLOVResponse>
) =>
  useApiQuery<GetVignetteRangeStatusLOVResponse>(
    getVignetteRangeStatusLOV,
    'getVignetteRangeStatusLOV',
    {
      ...options,
      fetchPolicy: 'cache-and-network'
    },
    { withLoader: false }
  );

const useGetVignetteRangeDetails = (
  options?: QueryHookOptions<
    GetVignetteRangeDetailsResponse,
    GetVignetteRangeDetailsVariables
  >
) =>
  useApiQuery<
    GetVignetteRangeDetailsResponse,
    GetVignetteRangeDetailsVariables
  >(
    getVignetteRangeDetails,
    'getVignetteRangeDetails',
    {
      ...options,
      fetchPolicy: 'cache-and-network',
      skip: !options?.variables?.id
    },
    { withLoader: false }
  );

const useGetZonesList = (options?: QueryHookOptions<GetZonesListResponse>) =>
  useApiQuery<GetZonesListResponse>(
    getZonesList,
    'getZonesList',
    {
      ...options,
      fetchPolicy: 'cache-and-network'
    },
    { withLoader: false }
  );

const useGetProviderLovsList = (
  options?: QueryHookOptions<GetProviderLovsListResponse>
) =>
  useApiQuery<GetProviderLovsListResponse>(
    getProviderLOVs,
    'getProviderLOVs',
    {
      ...options,
      fetchPolicy: 'cache-and-network'
    },
    { withLoader: false }
  );

const useGetActivePersonsList = (
  options?: QueryHookOptions<GetActivePersonsListResponse>
) =>
  useApiQuery<GetActivePersonsListResponse>(
    getActivePersonsList,
    'getActivePersonsList',
    {
      ...options,
      fetchPolicy: 'cache-and-network'
    },
    { withLoader: false }
  );

const useGetBusinessUserLovs = (
  options?: QueryHookOptions<GetBusinessUserLovsResponse>
) =>
  useApiQuery<GetBusinessUserLovsResponse>(
    getBusinessUserLovs,
    'getBusinessUserLovs',
    {
      ...options,
      fetchPolicy: 'cache-and-network'
    },
    { withLoader: false }
  );

const useGetPersonDetailsById = (
  options?: QueryHookOptions<
    GetPersonDetailsByIdResponse,
    GetPersonDetailsByIdVariables
  >
) =>
  useApiLazyQuery<GetPersonDetailsByIdResponse, GetPersonDetailsByIdVariables>(
    getPersonDetailsById,
    'getPersonDetailsById',
    {
      ...options,
      fetchPolicy: 'cache-and-network',
      skip: !options?.variables?.personId
    }
  );

const useCheckUserByPerson = (
  options?: QueryHookOptions<
    CheckBusinessUserByPersonResponse,
    CheckBusinessUserByPersonVariables
  >
) =>
  useApiLazyQuery<
    CheckBusinessUserByPersonResponse,
    CheckBusinessUserByPersonVariables
  >(checkBusinessUserByPerson, 'checkBusinessUserByPerson', {
    ...options,
    fetchPolicy: 'cache-and-network'
  });

const useGetPersonEmailDetailsInfo = (
  options?: QueryHookOptions<
    GetPersonEmailDetailsInfoResponse,
    GetPersonEmailDetailsInfoVariables
  >
) =>
  useApiLazyQuery<
    GetPersonEmailDetailsInfoResponse,
    GetPersonEmailDetailsInfoVariables
  >(getPersonEmailDetailsInfo, 'getPersonEmailDetailsInfo', {
    ...options,
    fetchPolicy: 'cache-and-network'
  });

const useUpatePersonInfo = (personId: string) =>
  useApiMutation<UpdatePersonInfoResponse, UpdatePersonInfoVariables>(
    updatePersonInfo,
    'updatePersonInfo',
    { successMsg: 'Person updated successfully!' },
    {
      refetchQueries: [
        { query: getPersonDetailsInfo, variables: { id: personId } }
      ],
      awaitRefetchQueries: true
    }
  );

const useUpatePersonAdditionalInfo = (personId: string) =>
  useApiMutation<
    UpdatePersonAdditionalInfoResponse,
    UpdatePersonAdditionalInfoVariables
  >(
    updatePersonAdditionalInfo,
    'updatePersonInfo',
    { successMsg: 'Person Additional updated successfully!' },
    {
      refetchQueries: [
        { query: getPersonDetailsInfo, variables: { id: personId } }
      ],
      awaitRefetchQueries: true
    }
  );

const useCreatePersonAddress = (
  personId: string,
  filter: {
    pagination: PaginationInput;
    OrderBy: OrderByInput;
  }
) =>
  useApiMutation<CreatePersonAddressResponse, CreatePersonAddressVariables>(
    createPersonAddress,
    'createPersonAddress',
    { successMsg: 'Person address created successfully!' },
    {
      refetchQueries: [
        {
          query: getPersonDetailsAddressList,
          variables: {
            personId,
            pagination: filter.pagination,
            OrderBy: filter.OrderBy
          }
        }
      ],
      awaitRefetchQueries: true
    }
  );

const useUpdatePersonAddress = (
  personId: string,
  filter: {
    pagination: PaginationInput;
    OrderBy: OrderByInput;
  }
) =>
  useApiMutation<UpdatePersonAddressResponse, UpdatePersonAddressVariables>(
    updatePersonAddress,
    'updatePersonAddress',
    { successMsg: 'Person address updated successfully!' },
    {
      refetchQueries: [
        {
          query: getPersonDetailsAddressList,
          variables: {
            personId,
            pagination: filter.pagination,
            OrderBy: filter.OrderBy
          }
        }
      ],
      awaitRefetchQueries: true
    }
  );

const useCreateBusinessPartner = () =>
  useApiMutation<CreateBusinessPartnerResponse, CreateBusinessPartnerVariables>(
    createBusinessPartner,
    'createBusinessPartner',
    { successMsg: 'Business Partner created successfully!' },
    {
      refetchQueries: [
        {
          query: getBusinessPartnersList,
          variables: {}
        }
      ],
      awaitRefetchQueries: true
    }
  );

const useCreateVignetteRange = (businessPartnerId: string) =>
  useApiMutation<
    CreateBusinessPartnerVignetteRangeResponse,
    CreateBusinessPartnerVignetteRangeVariables
  >(
    createBusinessPartnerVignetteRange,
    'createBusinessPartnerVignetteRange',
    { successMsg: 'Vignette Range assigned successfully!' },
    {
      refetchQueries: [
        {
          query: getBusinessPartnerVignetteRangeList,
          variables: {
            businessPartnerId,
            OrderBy: {
              descending: false,
              attribute: 'VignetteYear'
            },
            pagination: {
              pageSize: 10,
              pageNumber: 1
            }
          }
        }
      ],
      awaitRefetchQueries: true
    }
  );

const useUpdateVignetteRange = (
  options?: MutationHookOptions<
    UpdateBusinessPartnerVignetteRangeResponse,
    UpdateBusinessPartnerVignetteRangeVariables
  >
) =>
  useApiMutation<
    UpdateBusinessPartnerVignetteRangeResponse,
    UpdateBusinessPartnerVignetteRangeVariables
  >(
    updateBusinessPartnerVignetteRange,
    'updateBusinessPartnerVignetteRange',
    { successMsg: 'Vignette Range updated successfully!' },
    {
      refetchQueries: [
        {
          query: getBusinessPartnerDetails,
          variables: {
            id: options?.variables?.modifyBusinessPartnerVignetteInput
              ?.businessPartnerID
          }
        }
      ],
      awaitRefetchQueries: true
    }
  );

const useUpdateBusinessPartner = (businessPartnerId?: string) =>
  useApiMutation<UpdateBusinessPartnerResponse, UpdateBusinessPartnerVariables>(
    updateBusinessPartner,
    'updateBusinessPartner',
    { successMsg: 'Business Partner updated successfully!', withLoader: false },
    {
      refetchQueries: [
        {
          query: getBusinessPartnerDetails,
          variables: { id: businessPartnerId }
        }
      ],
      awaitRefetchQueries: true
    }
  );

const useUpdateUser = (userId?: string) =>
  useApiMutation<UpdateUserResult, UpdateUserVariables>(
    updateUser,
    'updateUser',
    { successMsg: 'User was modified successfully!', withLoader: false },
    {
      refetchQueries: [
        {
          query: getPersonDetailsById,
          variables: { id: userId }
        }
      ],
      awaitRefetchQueries: true
    }
  );

const useCreateUser = () =>
  useApiMutation<CreateUserResult, CreateUserVariables>(
    createUser,
    'createUser',
    { successMsg: 'User created successfully!', withLoader: false },
    {
      refetchQueries: [
        {
          query: getUsersList
        }
      ],
      awaitRefetchQueries: true
    }
  );

const useCreateProvider = () =>
  useApiMutation<CreateProviderResponse, CreateProviderVariables>(
    createProvider,
    'createProvider',
    {
      successMsg: 'Provider created successfully!',
      withLoader: false,
      buttonLoader: true
    },
    {
      refetchQueries: [
        {
          query: getProvidersList,
          variables: {
            pagination: {
              pageSize: 10,
              pageNumber: 1
            },
            orderBy: {
              attribute: 'createdOn',
              descending: false
            }
          }
        }
      ],
      awaitRefetchQueries: true
    }
  );

const useCreateAccoutingTransfer = (companyId: string) =>
  useApiMutation<
    CreateAccoutingTransferResponse,
    CreateAccoutingTransferVariables
  >(createAccountingTransfer, 'createAccountingTransfer', {
    successMsg: 'Transfer was created successfully!',
    withLoader: false,
    buttonLoader: true
  });

export const useGetAccountingTransferLovsList = (
  options?: QueryHookOptions<GetAccountingTransferLovsResponse>
) =>
  useApiQuery<GetAccountingTransferLovsResponse>(
    getAccountingTransferLovs,
    'getAccountingTransferLovs',
    {
      ...options,
      fetchPolicy: 'cache-and-network'
    },
    { withLoader: false }
  );

export type GetAmendmentSubtypesResponse = {
  production: {
    getAmendmentSubTypes: { id: string; code: string; title: string }[];
  };
};

export const useGetAmendmentSubtypes = (
  options?: QueryHookOptions<GetAmendmentSubtypesResponse>
) =>
  useApiQuery<GetAmendmentSubtypesResponse>(
    getAmendmentSubtypesQuery,
    'getAmendmentSubTypes',
    { ...options, fetchPolicy: 'cache-and-network' },
    { withLoader: false }
  );

export const useCreateAmendment = () =>
  useApiMutation<CreateAmendmentResponse, CreateAmendmentVariables>(
    createAmendmentMutation,
    'createAmendment',
    {
      successMsg: 'Amendment created successfully!',
      withLoader: false,
      buttonLoader: true
    },
    {
      awaitRefetchQueries: true
    }
  );

const useCreateCancellationAmendment = () =>
  useApiMutation<CreateCancellationAmendmentResponse, CreateAmendmentVariables>(
    createCancellationAmendmentMutation,
    'createCancellationAmendment',
    {
      successMsg: 'Cancellation amendment created successfully!',
      withLoader: false,
      buttonLoader: true
    },
    {
      awaitRefetchQueries: true
    }
  );

const useGetSystemReferenceDate = (
  options?: QueryHookOptions<GetSystemReferenceDateResponse>
) =>
  useApiQuery<GetSystemReferenceDateResponse>(
    getSystemReferenceDateQuery,
    'getSystemReferenceDate',
    { ...options, fetchPolicy: 'cache-and-network' },
    { withLoader: false }
  );

const useGetClosedMonth = (
  options?: QueryHookOptions<GetClosedMonthResponse, GetClosedMonthVariables>
) =>
  useApiQuery<GetClosedMonthResponse, GetClosedMonthVariables>(
    getClosedMonthQuery,
    'GetClosedMonth',
    { ...options, fetchPolicy: 'cache-and-network' },
    { withLoader: false }
  );

const useCloseMonth = (closingType: string) =>
  useApiMutation<CloseMonthResponse, CloseMonthVariables>(
    closeMonth,
    'closeMonth',
    {
      successMsg: `${closingType} Month Closing applied successfully!`,
      withLoader: false,
      buttonLoader: true
    },
    {
      refetchQueries: [searchClosedMonths],
      awaitRefetchQueries: true
    }
  );

const useGenerateCloseMonthReport = (closingType: string) =>
  useApiMutation<CloseMonthResponse, CloseMonthVariables>(
    generateCloseMonthReport,
    'generateCloseMonthReport',
    {
      successMsg: `${closingType} Month Closing report generated successfully!`,
      withLoader: false,
      buttonLoader: true
    },
    {
      awaitRefetchQueries: true
    }
  );
const useGetLineRefDate = (lineCode: string) =>
  useApiQuery<GetLineRefDateResponse>(
    getLineRefDate,
    'getLineRefDate',
    {
      variables: { lineCode },
      fetchPolicy: 'cache-and-network',
      skip: !lineCode
    },
    { withLoader: false }
  );

const useGetPolicyPersonEnums = (
  options?: QueryHookOptions<GetPersonDetailsFormResponse>
) =>
  useApiQuery<any>(
    getPolicyPersonEnums(),
    'getPolicyPersonEnums',
    { ...options, fetchPolicy: 'cache-and-network' },
    { withLoader: false }
  );

const useCreateClaimPerson = () =>
  useApiMutation<CreateClaimPersonResponse, CreateClaimPersonVariables>(
    createClaimPerson(),
    'createClaimPerson',
    {
      successMsg: 'Person added successfully!',
      withLoader: false,
      buttonLoader: true
    }
  );

export {
  useCheckUserByPerson,
  useCloseMonth,
  useCreateAccoutingTransfer,
  useCreateBusinessPartner,
  useCreateCancellationAmendment,
  useCreateClaimPerson,
  useCreatePersonAddress,
  useCreateProvider,
  useCreateUser,
  useCreateVignetteRange,
  useGenerateCloseMonthReport,
  useGetActivePersonsList,
  useGetActivePersonsWithoutBusinessPartnersList,
  useGetAdditionalInformationEnums,
  useGetBusinessPartnerTypeEnumList,
  useGetBusinessUserLovs,
  useGetClosedMonth,
  useGetCountriesList,
  useGetCurrenciesList,
  useGetLineRefDate,
  useGetPersonAddressDetails,
  useGetPersonAddressLovs,
  useGetPersonDetails,
  useGetPersonDetailsById,
  useGetPersonDetailsForm,
  useGetPersonEmailDetailsInfo,
  useGetPolicyPersonEnums,
  useGetProviderLovsList,
  useGetRelatedCompaniesList,
  useGetSystemReferenceDate,
  useGetTitlesList,
  useGetVignetteRangeDetails,
  useGetVignetteRangeStatusLOV,
  useGetZonesList,
  useUpatePersonAdditionalInfo,
  useUpatePersonInfo,
  useUpdateBusinessPartner,
  useUpdatePersonAddress,
  useUpdateUser,
  useUpdateVignetteRange
};
